import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FLEX_CONTAINER_COLUMN, FLEX_ITEM, H_100, PADDING, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { CustomElement, WebPageComponent } from "@cervval.js/client-html5-spa/navigation";
import { CjsDayAgenda } from "@cervval.js/client-html5-ui/src/components/agenda/dayAgenda/dayAgenda/dayAgenda";
import { BasicDayAgendaModel } from "@cervval.js/client-html5-ui/src/components/agenda/dayAgenda/dayAgenda/dayAgendaModel";
import { AgendaItemStyle } from "@cervval.js/client-html5-ui/src/components/agenda/dayAgenda/dayAgenda/dayAgendaOptions";
import { CjsSmartTable } from "@cervval.js/client-html5-ui/src/components/smartTable/smartTable";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { toTimestamp, UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { NOW, TODAY } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { add, beginOf } from "@cervval.js/common/src/tools/timestamps.tools.tsdate";
import { translateHTMLReferences } from "@ehealth_tools/client-html-tools/src/tools/webcomponent.tools";
import { BookingSession, BookingUserSession } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { isProfessional } from "@ehealth_tools/common-booking/src/data/booking.tools";
import { BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { filterMapToSet } from "@ehealth_tools/common/src/tools/data.tools";
import { AppointmentCard } from "../../../components/appointmentCard/appointmentCard";
import { AbstractClientBookingService } from "../../../services/abstractClientBooking.service";
import { I18nAgenda } from "./agendaPage.i18n";
interface AgendaColumn {
    uid: ColumnUID;
    label: string;
    dayTs: UnixTimestamp;
}

type ColumnUID = string;

interface AgendaItem {
    session: BookingSession;
    uid: string;
    start: UnixTimestamp;
    finish: UnixTimestamp;
    columnUids: ColumnUID[];
    label: string;
}

export interface AgendaOptions {
    onAccept?: () => Promise<void>;
}

const defaultAgendaOptions: AgendaOptions = {
    //
};

@CustomElement({
    name: "agenda-page",
    style: require("./agendaPage.scss"),
    template: require("./agendaPage.html"),
    i18n: require("./agendaPage.i18n").I18n(),
    elementCssClasses: [W_100, H_100, FLEX_CONTAINER_COLUMN, FLEX_ITEM, PADDING.PY_2]
})
export class Agenda<I18n extends I18nAgenda = I18nAgenda> extends WebPageComponent<I18n> {
    @TypedHtmlElements({ ensureLoadedClasses: [CjsDayAgenda, CjsSmartTable] })
    public readonly components!: {
        container: HTMLDivElement,
        myAgenda: CjsDayAgenda<AgendaItem, AgendaColumn>,
        appointments: HTMLDivElement,
        myRdv: HTMLHeadElement
    };

    @Injected(BOOKING_SERVICE)
    private _bookingService!: AbstractClientBookingService;

    @TypedWebComponentOptions(defaultAgendaOptions)
    public readonly options!: WebComponentOptions<AgendaOptions>;

    private _items: AgendaItem[] = [];
    private _cols: AgendaColumn[] = [];

    constructor(options?: AgendaOptions) {
        super();
        this.options.setWithoutRefresh(options);
    }

    /** @inheritdoc */
    public override init(): void {
        translateHTMLReferences(this.components, this.i18n.html);
        this.components.myAgenda.options.setAndRefreshIfChanged({
            rowHeight: 80,
            workCalendarHelper: this._bookingService.getUserWorkCalendarHelper(this._bookingService.getCurrentUser().id) ?? undefined
        });
    }

    /** @inheritdoc */
    public override refresh(): void {
        this._buildData();
        this.components.myAgenda.setData({
            dayAgendaModel: this._getAgendaModel(),
            items: this._items,
            columns: this._cols
        });

        this._refreshAppointmentCards();
    }

    /** Build the data for the agenda -- I cannot tell more : it is messy */
    private _buildData(): void {
        // By default, we have our sessions (as a professional), and sessions shared by other professionals
        const sessions = this._bookingService.getData<BookingSession>("bookingSessions");
        // By default, we have our sessions (as a customer), and user sessions shared by other users
        const userSessions = this._bookingService.getData<BookingUserSession>("bookingUserSessions");

        const currentUserId = this._bookingService.getCurrentUser().id;
        /** Id set of all sessions in which we are a customer */
        const sessionAsCustomerIdSet = filterMapToSet(userSessions, (userSession) => userSession.customerId === currentUserId, "sessionId");
        // We keep all sessions we have as a professional as well as all sessions we have as a customer
        const ownSessions = sessions.filter((session) => session.professionalId === currentUserId || sessionAsCustomerIdSet.has(session.id));

        this._items = [];
        this._cols = [];

        let minDate: UnixTimestamp = TODAY();
        let maxDate: UnixTimestamp = TODAY();

        const colMap: Map<UnixTimestamp, AgendaColumn> = new Map();

        for (const session of ownSessions) {
            const finish = add(session.start, { second: session.duration }).uts;
            minDate = toTimestamp(Math.min(minDate, session.start));
            maxDate = toTimestamp(Math.max(maxDate, finish));
            let currentDay = beginOf(session.start, "day");
            const lastDay = beginOf(finish, "day");

            while (currentDay <= lastDay) {
                this._items.push({
                    uid: `i_${session.id}`,
                    start: session.start,
                    finish: add(session.start, { second: session.duration }).uts,
                    label: this._bookingService.getSessionLabel(session),
                    session,
                    columnUids: [`c_${currentDay}`]
                });
                colMap.set(currentDay.uts, {
                    dayTs: currentDay.uts,
                    label: "",
                    uid: `c_${currentDay}`
                });
                currentDay = beginOf(add(currentDay, { day: 1 }), "day");
            }
        }
        const cols = [...colMap.values()];
        cols.sort((a, b) => a.dayTs - b.dayTs);
        this._cols = cols;
    }

    /** Build the agenda model to interpret provided data (above) */
    private _getAgendaModel(): BasicDayAgendaModel<AgendaItem, AgendaColumn> {
        // Agenda model
        const defaultAgendaModel = new BasicDayAgendaModel<AgendaItem, AgendaColumn>({
            itemUid: "uid",
            itemStartTs: "start",
            itemFinishTs: "finish",
            itemColumnUids: "columnUids",
            itemText: "label",
            itemTooltip: (item) => this._bookingService.getSessionTooltip(item.session),
            itemTooltipOptions: () => { return { delay: 200 }; },
            columnUid: "uid",
            columnDay: "dayTs",
            columnLabel: "label",
            isEditable: false
        });

        defaultAgendaModel.getItemStyle = (item) => {
            const data = this._bookingService.getSessionData(item.session.id);
            const acceptedByAll = data.session.accepted != null && !data.userSessions.some(v => v.accepted == null);
            const style: AgendaItemStyle = {
                background: acceptedByAll ? "#71beda" : "#bbe5f4",
                borderColor: "#61a8c1",
                borderRadius: 2,
                borderStyle: acceptedByAll ? "solid" : "dashed",
                borderWidth: 2,
                handleHoverColor: "#aaa",
                selectedBackground: undefined,
                selectedBorder: undefined,
                selectedHandleColor: "faa",
                stripeColor: "#666",
                textColor: "#fff",
                stripe: false
            };
            return style;
        };

        return defaultAgendaModel;
    }

    /** */
    private _refreshAppointmentCards(): void {
        const userSessions: BookingUserSession[] = [];
        if (isProfessional(this._bookingService.getCurrentUser())) {
            const sessions = this._bookingService.getData<BookingSession>("bookingSessions").filter(v => v.professionalId === this._bookingService.getCurrentUser().id);
            const sessionIds = sessions.map(v => v.id);
            userSessions.push(...this._bookingService.getData<BookingUserSession>("bookingUserSessions").filter(v => sessionIds.includes(v.sessionId)));
        } else {
            userSessions.push(...this._bookingService.getData<BookingUserSession>("bookingUserSessions").filter(v => v.customerId === this._bookingService.getCurrentUser().id));
        }
        this.components.appointments.innerHTML = "";
        for (const userSession of userSessions) {
            const session = this._bookingService.getById<BookingSession>("bookingSessions", userSession.sessionId);
            if (session.start >= NOW()) {
                this.components.appointments.append(new AppointmentCard({ userSession, onDelete: () => this.refresh(), onAccept: () => { this.options.onAccept?.(); this.refresh(); } }));
            }
        }
    }
}
